import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Plus, Search, SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Empty, PageHeader, StatusBadge } from "@/components/shared";
import { PricingRules } from "@/components/pricing/PricingRules";
import { fdate, inr, label, useApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { can } from "@/lib/roles";

const STATUSES = ["DRAFT", "SENT_TO_CLIENT", "APPROVED_BY_CLIENT", "SUPERSEDED"];

function QuotesTable({ quotes, showMargin }) {
  const nav = useNavigate();
  const heads = ["Quote", "Client", "Lead", "Status", "Grand total", ...(showMargin ? ["Margin"] : []), "Gift", "Created by", "Updated"];
  if (!quotes.length) return <Empty text="No quotations match your filters." />;
  return (
    <div className="rounded-lg border bg-card overflow-x-auto">
      <table className="w-full text-sm" data-testid="quotes-table">
        <thead><tr className="border-b bg-muted/40 text-xs text-muted-foreground">
          {heads.map((h) => <th key={h} className="text-left font-medium px-4 py-2.5 whitespace-nowrap">{h}</th>)}
        </tr></thead>
        <tbody>
          {quotes.map((q) => (
            <tr key={q.id} onClick={() => nav(q.leadId ? `/leads/${q.leadId}/quote-builder?quote=${q.id}` : `/quotes/builder?quote=${q.id}`)} className="border-b last:border-0 cursor-pointer hover:bg-secondary/30" data-testid={`quote-row-${q.quoteCode}`}>
              <td className="px-4 py-3 whitespace-nowrap"><div className="font-mono text-xs font-semibold" data-testid={`quote-no-${q.quoteCode}`}>{q.quoteNo || q.quoteCode} · Rev-{q.versionNumber}</div><div className="font-mono text-[11px] text-muted-foreground">{q.quoteCode}</div></td>
              <td className="px-4 py-3"><div className="font-medium">{q.clientName || <span className="text-xs rounded border px-1.5 py-0.5 text-muted-foreground" data-testid={`quote-unassigned-${q.quoteCode}`}>Unassigned</span>}</div><div className="text-xs text-muted-foreground">{q.clientCity}</div></td>
              <td className="px-4 py-3 font-mono text-xs">{q.leadNumber || "—"}</td>
              <td className="px-4 py-3"><StatusBadge status={q.status} /></td>
              <td className="px-4 py-3 num whitespace-nowrap">{inr(q.grandTotal)}</td>
              {showMargin && <td className="px-4 py-3 num whitespace-nowrap" data-testid={`quote-margin-${q.quoteCode}`}>{q.netProfitMarginPct != null ? `${q.netProfitMarginPct.toFixed(1)}%` : "—"}</td>}
              <td className="px-4 py-3 text-xs">{q.eligibleGifts?.[0] || "—"}</td>
              <td className="px-4 py-3 text-xs">{q.createdBy}</td>
              <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{fdate(q.updatedAt || q.createdAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Quotes() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { data, loading } = useApi("/quotations");
  const error = !loading && !data;
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("ALL");
  const pricing = can(user, "pricing:manage");
  const rows = useMemo(() => (data || []).filter((x) => (status === "ALL" || x.status === status)
    && [x.quoteNo, x.quoteCode, x.clientName, x.leadNumber].filter(Boolean).join(" ").toLowerCase().includes(q.toLowerCase())), [data, q, status]);
  return (
    <div>
      <PageHeader title="Quotations" subtitle="Every quote across all leads, plus the pricing rules that drive them.">
        <Button onClick={() => nav("/quotes/builder")} data-testid="new-quick-quote-button"><Plus className="h-4 w-4 mr-2" />New quick quote</Button>
      </PageHeader>
      <Tabs defaultValue="quotes">
        <TabsList className="mb-5">
          <TabsTrigger value="quotes" data-testid="quotes-tab-list"><FileText className="h-4 w-4 mr-1.5" />All quotations</TabsTrigger>
          {pricing && <TabsTrigger value="pricing" data-testid="quotes-tab-pricing"><SlidersHorizontal className="h-4 w-4 mr-1.5" />Pricing rules</TabsTrigger>}
        </TabsList>
        <TabsContent value="quotes">
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative sm:w-80"><Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-9" placeholder="Search quote, client or lead" value={q} onChange={(e) => setQ(e.target.value)} data-testid="quotes-search-input" /></div>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="sm:w-56" data-testid="quotes-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All statuses</SelectItem>
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{label(s)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {error ? <Empty text="Couldn't load quotations. Please try again." /> : !data
            ? <div className="text-sm text-muted-foreground py-10 text-center" data-testid="quotes-loading">Loading quotations…</div>
            : <QuotesTable quotes={rows} showMargin={pricing} />}
        </TabsContent>
        {pricing && <TabsContent value="pricing"><PricingRules /></TabsContent>}
      </Tabs>
    </div>
  );
}
